// src/services/companyService.ts
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';
import { getAuth } from 'firebase/auth';

const COMPANY_PROFILE_COLLECTION = 'companyProfiles';

export interface CompanyAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface CompanyContactInfo {
  phone: string;
  email: string;
  website: string;
  fax: string;
}

export interface CompanyLocation {
  address: string;
  latitude: number;
  longitude: number;
}

export interface CompanySocialMedia {
  facebook: string;
  instagram: string;
  threads: string;
}

export interface CompanyProfile {
  id?: string;
  companyName: string;
  businessType: string;
  registrationNumber: string;
  taxId: string;
  address: CompanyAddress;
  contactInfo: CompanyContactInfo;
  location: CompanyLocation;
  socialMedia: CompanySocialMedia;
  currency: string;
  timezone: string;
  fiscalYearStart: string;
  taxPercentage: number;
  logo: string;
  notes: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
  updatedBy?: string;
}

export class CompanyService {
  /**
   * Get the company profile
   */
  static async getCompanyProfile(): Promise<CompanyProfile | null> {
    try {
      const companiesRef = collection(db, COMPANY_PROFILE_COLLECTION);
      const snapshot = await getDocs(companiesRef);

      if (snapshot.empty) {
        return null;
      }

      const docSnap = snapshot.docs[0];
      const data = docSnap.data();

      return {
        id: docSnap.id,
        companyName: data.companyName || '',
        businessType: data.businessType || '',
        registrationNumber: data.registrationNumber || '',
        taxId: data.taxId || '',
        address: data.address || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        contactInfo: data.contactInfo || {
          phone: '',
          email: '',
          website: '',
          fax: '',
        },
        location: data.location || {
          address: '',
          latitude: 0,
          longitude: 0,
        },
        socialMedia: data.socialMedia || {
          facebook: '',
          instagram: '',
          threads: '',
        },
        currency: data.currency || 'USD',
        timezone: data.timezone || 'America/New_York',
        fiscalYearStart: data.fiscalYearStart || '01-01',
        taxPercentage: data.taxPercentage || 0,
        logo: data.logo || '',
        notes: data.notes || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      };
    } catch (error: any) {
      console.error('Error fetching company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access company data.');
      }
      throw error;
    }
  }

  /**
   * Get company profile by ID
   */
  static async getCompanyProfileById(companyId: string): Promise<CompanyProfile | null> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('You must be logged in to view company profile');
      }

      const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, companyId);
      const companySnap = await getDoc(companyRef);

      if (!companySnap.exists()) {
        return null;
      }

      const data = companySnap.data();

      return {
        id: companySnap.id,
        companyName: data.companyName || '',
        businessType: data.businessType || '',
        registrationNumber: data.registrationNumber || '',
        taxId: data.taxId || '',
        address: data.address || {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        contactInfo: data.contactInfo || {
          phone: '',
          email: '',
          website: '',
          fax: '',
        },
        location: data.location || {
          address: '',
          latitude: 0,
          longitude: 0,
        },
        socialMedia: data.socialMedia || {
          facebook: '',
          instagram: '',
          threads: '',
        },
        currency: data.currency || 'USD',
        timezone: data.timezone || 'America/New_York',
        fiscalYearStart: data.fiscalYearStart || '01-01',
        taxPercentage: data.taxPercentage || 0,
        logo: data.logo || '',
        notes: data.notes || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      };
    } catch (error: any) {
      console.error('Error fetching company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access company data.');
      }
      throw error;
    }
  }

  /**
   * Create a new company profile
   */
  static async createCompanyProfile(profile: CompanyProfile): Promise<string> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('You must be logged in to create a company profile');
      }

      const existingProfile = await this.getCompanyProfile();
      if (existingProfile) {
        throw new Error('A company profile already exists. Please update the existing profile instead.');
      }

      const companyRef = doc(collection(db, COMPANY_PROFILE_COLLECTION));

      await setDoc(companyRef, {
        companyName: profile.companyName,
        businessType: profile.businessType || '',
        registrationNumber: profile.registrationNumber || '',
        taxId: profile.taxId || '',
        address: profile.address,
        contactInfo: profile.contactInfo,
        location: profile.location || {
          address: '',
          latitude: 0,
          longitude: 0,
        },
        socialMedia: profile.socialMedia || {
          facebook: '',
          instagram: '',
          threads: '',
        },
        currency: profile.currency || 'USD',
        timezone: profile.timezone || 'America/New_York',
        fiscalYearStart: profile.fiscalYearStart || '01-01',
        taxPercentage: profile.taxPercentage || 0,
        logo: profile.logo || '',
        notes: profile.notes || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedBy: currentUser.uid,
      });

      return companyRef.id;
    } catch (error: any) {
      console.error('Error creating company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to create company data.');
      }
      throw error;
    }
  }

  /**
   * Update existing company profile
   */
  static async updateCompanyProfile(profile: CompanyProfile): Promise<void> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('You must be logged in to update company profile');
      }

      const existingProfile = await this.getCompanyProfile();
      if (!existingProfile || !existingProfile.id) {
        throw new Error('No company profile found to update');
      }

      const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);

      await updateDoc(companyRef, {
        companyName: profile.companyName,
        businessType: profile.businessType || '',
        registrationNumber: profile.registrationNumber || '',
        taxId: profile.taxId || '',
        address: profile.address,
        contactInfo: profile.contactInfo,
        location: profile.location || {
          address: '',
          latitude: 0,
          longitude: 0,
        },
        socialMedia: profile.socialMedia || {
          facebook: '',
          instagram: '',
          threads: '',
        },
        currency: profile.currency || 'USD',
        timezone: profile.timezone || 'America/New_York',
        fiscalYearStart: profile.fiscalYearStart || '01-01',
        taxPercentage: profile.taxPercentage || 0,
        logo: profile.logo || '',
        notes: profile.notes || '',
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
    } catch (error: any) {
      console.error('Error updating company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update company data.');
      }
      throw error;
    }
  }

  /**
   * Delete company profile (soft delete via isActive flag)
   */
  static async deleteCompanyProfile(): Promise<void> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('You must be logged in to delete company profile');
      }

      const existingProfile = await this.getCompanyProfile();
      if (!existingProfile || !existingProfile.id) {
        throw new Error('No company profile found to delete');
      }

      const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);

      await updateDoc(companyRef, {
        isActive: false,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
    } catch (error: any) {
      console.error('Error deleting company profile:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to delete company data.');
      }
      throw error;
    }
  }

  /**
   * Validate fiscal year start format (MM-DD)
   */
  static validateFiscalYearStart(fiscalYearStart: string): boolean {
    const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
    return regex.test(fiscalYearStart);
  }

  /**
   * Validate tax percentage (0–100)
   */
  static validateTaxPercentage(taxPercentage: number): boolean {
    return taxPercentage >= 0 && taxPercentage <= 100;
  }

  /**
   * Calculate tax amount
   */
  static calculateTax(subtotal: number, taxPercentage: number): number {
    return (subtotal * taxPercentage) / 100;
  }

  /**
   * Calculate total with tax
   */
  static calculateTotalWithTax(subtotal: number, taxPercentage: number): number {
    return subtotal + this.calculateTax(subtotal, taxPercentage);
  }

  /**
   * Format currency using Intl
   */
  static formatCurrency(amount: number, currencyCode: string): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch (error) {
      return `${currencyCode} ${amount.toFixed(2)}`;
    }
  }

  /**
   * Get company display name
   */
  static async getCompanyDisplayName(): Promise<string> {
    try {
      const profile = await this.getCompanyProfile();
      return profile?.companyName || 'Company';
    } catch (error) {
      console.error('Error getting company display name:', error);
      return 'Company';
    }
  }
}